import {useCallback, useMemo, useState} from 'react';

import {InternalSurveyModal} from 'shells/modal/internal-survey-modal/internal-survey-modal';

import {FEEDBACK_SURVEYS} from '../../internal-feedback/feedback-surveys';
import {useFeedbackSubmit} from '../../internal-feedback/graphql/hooks/use-feedback-submit';
import {getDisplayProps, isSurveyEnabled} from '../../internal-feedback/helpers';

interface UsePeepRedesignSurveyOptions {
    isAdmin?: boolean;
    onClose?: () => void;
}

interface UsePeepRedesignSurveyResult {
    isOpen: boolean;
    openSurvey: () => void;
    SurveyModal: React.ReactNode;
}

export function usePeepRedesignSurvey(
    options?: UsePeepRedesignSurveyOptions
): UsePeepRedesignSurveyResult {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const {feedbackSubmit} = useFeedbackSubmit();

    const survey = useMemo(() => {
        const peepRedesignSurvey = FEEDBACK_SURVEYS.find(
            (item) => item.id === 'peep-redesign-beta'
        );

        if (!peepRedesignSurvey || !isSurveyEnabled(peepRedesignSurvey)) {
            return null;
        }

        return peepRedesignSurvey;
    }, []);
    const displayProps = survey ? getDisplayProps(survey, Boolean(options?.isAdmin)) : null;

    const openSurvey = () => {
        setIsOpen(true);
    };

    const closeSurvey = () => {
        setIsOpen(false);
        options?.onClose?.();
    };

    const handleSubmit = useCallback(
        (response: string) => {
            if (!survey) {
                return Promise.resolve();
            }

            return feedbackSubmit({
                key: survey.id,
                response,
                uri: window.location.href,
            });
        },
        [feedbackSubmit, survey]
    );

    const SurveyModal =
        isOpen && survey && displayProps ? (
            <InternalSurveyModal
                onClose={closeSurvey}
                title={displayProps.surveyTitle}
                description={displayProps.surveySubtitle}
                thumbnailIllustration={displayProps.surveyImage || undefined}
                thumbnailBackgroundColor={displayProps.surveyImageBackgroundColor || undefined}
                onSubmit={handleSubmit}
            />
        ) : null;

    return {
        isOpen,
        openSurvey,
        SurveyModal,
    };
}
